import React from 'react';
import { Link } from 'react-router-dom';
import logo from '../images/new_logo.png';

const HowItWorks = () => {
  return (
    <div className="how-it-works-page">
      <main className="main-content">
        <section className="hero-section">
          <div className="hero-container">
            <h1>How BullBearly Works</h1>
            <p className="hero-subtitle">
              Transform your stock market research with AI-powered insights from RSS feeds, social media, and customizable prompts
            </p>
          </div>
        </section>

        <section className="integrated-workflow-section">
          <div className="workflow-container">
            <h2>How BullBearly Transforms Your Research</h2>
            <p className="workflow-subtitle">Follow this simple 4-step process to unlock AI-powered financial intelligence</p>
            
            <div className="integrated-steps">
              
              {/* Step 1: Connect Your Data Sources */}
              <div className="workflow-step-combined">
                <div className="step-header">
                  <div className="step-number">1</div>
                  <h3>Connect Your Data Sources</h3>
                  <p>Set up your information pipeline by connecting RSS feeds and social media sources</p>
                </div>
                
                <div className="step-features">
                  <div className="feature-mini-card">
                    <div className="feature-icon-mini">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" stroke="#2563eb" strokeWidth="2" fill="none"/>
                        <path d="M8 12h8M8 16h8M8 8h8" stroke="#2563eb" strokeWidth="2"/>
                      </svg>
                    </div>
                    <h4>RSS Feeds</h4>
                    <ul>
                      <li>Free financial news feeds</li>
                      <li>Premium paid subscriptions</li>
                      <li>Real-time market updates</li>
                    </ul>
                  </div>
                  
                  <div className="feature-mini-card">
                    <div className="feature-icon-mini">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z" stroke="#2563eb" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>
                    <h4>Social Media</h4>
                    <ul>
                      <li>X (Twitter) feeds</li>
                      <li>LinkedIn content</li>
                      <li>Reddit discussions</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step 2: Configure AI Analysis */}
              <div className="workflow-step-combined">
                <div className="step-header">
                  <div className="step-number">2</div>
                  <h3>Configure AI Analysis</h3>
                  <p>Customize prompts and choose your AI integration method for personalized insights</p>
                </div>
                
                <div className="step-features">
                  <div className="feature-mini-card">
                    <div className="feature-icon-mini">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="#2563eb" strokeWidth="2" fill="none"/>
                        <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="#2563eb" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>
                    <h4>Custom Prompts</h4>
                    <ul>
                      <li>Edit existing prompts</li>
                      <li>Create custom analysis</li>
                      <li>Industry-specific templates</li>
                    </ul>
                  </div>
                  
                  <div className="feature-mini-card">
                    <div className="feature-icon-mini">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" stroke="#2563eb" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>
                    <h4>API Options</h4>
                    <ul>
                      <li>Your own OpenAI key</li>
                      <li>Our managed key</li>
                      <li>Flexible pricing</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step 3: Get AI Insights */}
              <div className="workflow-step-combined">
                <div className="step-header">
                  <div className="step-number">3</div>
                  <h3>Get AI Insights</h3>
                  <p>Receive intelligent analysis powered by ChatGPT, tailored to your investment strategy</p>
                </div>
                
                <div className="step-features single-feature">
                  <div className="feature-mini-card wide">
                    <div className="feature-icon-mini">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" stroke="#2563eb" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>
                    <h4>AI-Powered Analysis</h4>
                    <ul>
                      <li>✅ Intelligent content summarization</li>
                      <li>✅ Sentiment analysis</li>
                      <li>✅ Custom alert triggers</li>
                      <li>✅ Market trend identification</li>
                      <li>✅ Company detection & analysis</li>
                      <li>✅ Risk assessment insights</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Step 4: Make Informed Decisions */}
              <div className="workflow-step-combined">
                <div className="step-header">
                  <div className="step-number">4</div>
                  <h3>Make Informed Decisions</h3>
                  <p>Use comprehensive market intelligence to stay ahead of the market</p>
                </div>
                
                <div className="step-features">
                  <div className="feature-mini-card">
                    <div className="feature-icon-mini">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="#2563eb" strokeWidth="2" fill="none"/>
                      </svg>
                    </div>
                    <h4>Real-time Alerts</h4>
                    <ul>
                      <li>Breaking news notifications</li>
                      <li>Market sentiment changes</li>
                      <li>Custom trigger events</li>
                    </ul>
                  </div>
                  
                  <div className="feature-mini-card">
                    <div className="feature-icon-mini">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M3 3h18v18H3zM9 9h6v6H9z" stroke="#2563eb" strokeWidth="2" fill="none"/>
                        <path d="M9 9l6 6M15 9l-6 6" stroke="#2563eb" strokeWidth="2"/>
                      </svg>
                    </div>
                    <h4>Smart Dashboard</h4>
                    <ul>
                      <li>Organized news feeds</li>
                      <li>Company analysis panels</li>
                      <li>Export capabilities</li>
                    </ul>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="cta-container">
            <h2>Ready to Transform Your Research?</h2>
            <p>Join the waitlist to be the first to experience AI-powered financial intelligence.</p>
            <Link to="/" className="cta-button">
              Join Waitlist
            </Link>
          </div>
        </section>

      </main>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src={logo} alt="BullBearly" className="footer-logo" />
              <span className="footer-brand-text">BullBearly</span>
            </div>
            <div className="footer-links">
              <Link to="/terms" className="footer-link">Terms of Service</Link>
              <Link to="/privacy" className="footer-link">Privacy Policy</Link>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 BullBearly, LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HowItWorks;
